import React, {useState} from 'react';
import {
    Building,
    Building2,
    Ship,
    Globe,
    Phone,
    Mail,
    ArrowRight,
    CheckCircle,
    Award,
    Clock,
    Users,
    DollarSign,
    Briefcase,
    MapPin,
    LineChart,
    ShieldCheck
} from 'lucide-react';
import {Helmet} from 'react-helmet-async';
import trial2 from "../../Assets/Images/image-section-purpose.webp";
import trial from '../../Assets/Images/trial/15- (1).webp'

const DubaiZonesInfo = () => {
    const [activeTab, setActiveTab] = useState('free-zones');
    const [selectedService, setSelectedService] = useState(null);

    const freeZones = [
        {
            name: "Commodia",
            location: "Strategic Business District",
            benefits: [
                "100% foreign ownership",
                "0% corporate and personal income tax",
                "Full repatriation of profits",
                "No currency restrictions",
                "Modern business infrastructure",
                "Premium facilities",
                "Strategic trade advantages"
            ],
            industries: ["Commodities", "Financial Services", "Professional Services", "Technology"],
            setupTime: "7-14 days"
        },
        {
            name: "Technology Free",
            location: "Innovation Hub",
            benefits: [
                "Tech-focused ecosystem",
                "World-class infrastructure",
                "Dynamic business community",
                "Strategic location",
                "Innovation facilities",
                "Industry events",
                "Growth programs"
            ],
            industries: ["Technology", "Software", "Internet Services", "E-commerce"],
            setupTime: "10-15 days"
        },
        {
            name: "Trading & Logistics",
            location: "Strategic Port District",
            benefits: [
                "Premium port access",
                "Comprehensive logistics network",
                "Industrial facilities",
                "Multiple visa options",
                "Customs benefits",
                "24/7 operations",
                "Storage solutions"
            ],
            industries: ["Logistics", "Trading", "Manufacturing", "E-commerce"],
            setupTime: "14-21 days"
        },
        {
            name: "Medical",
            location: "Healthcare District",
            benefits: [
                "Healthcare ecosystem access",
                "State-of-the-art facilities",
                "Research opportunities",
                "Medical education access",
                "100% foreign ownership",
                "Healthcare licensing support",
                "Medical tourism opportunities"
            ],
            industries: ["Healthcare", "Medical Services", "Pharmaceuticals", "Biotechnology"],
            setupTime: "21-30 days"
        },
        {
            name: "Media & Creative",
            location: "Creative Hub",
            benefits: [
                "Creative ecosystem",
                "Modern broadcasting facilities",
                "Industry community access",
                "Production support",
                "Content creation resources",
                "Networking opportunities",
                "Global media connections"
            ],
            industries: ["Media", "Broadcasting", "Publishing", "Advertising"],
            setupTime: "10-15 days"
        },
        {
            name: "Financial Services",
            location: "Financial District",
            benefits: [
                "Robust regulatory framework",
                "International legal system",
                "Premium financial infrastructure",
                "Global banking access",
                "Investment opportunities",
                "Legal protection",
                "International network"
            ],
            industries: ["Banking", "Finance", "Insurance", "Investment"],
            setupTime: "14-21 days"
        }
    ];

    const mainlandBenefits = [
        {
            category: "Business Operations",
            benefits: [
                "Freedom to conduct business across the UAE",
                "Direct access to the local market",
                "No geographical business restrictions",
                "Opportunity for government contracts"
            ]
        },
        {
            category: "Market Presence",
            benefits: [
                "Enhanced local credibility",
                "Flexibility in business activities",
                "Access to a wider range of business activities",
                "Strong brand presence in local market"
            ]
        },
        {
            category: "Legal Benefits",
            benefits: [
                "No requirement for annual audits",
                "Multiple branch options",
                "Easier bank account opening",
                "Local sponsorship support"
            ]
        },
        {
            category: "Growth Opportunities",
            benefits: [
                "Scalable business model",
                "Direct government tender participation",
                "Local partnership possibilities",
                "Expanded visa quota"
            ]
        }
    ];

    const offshoreBenefits = [
        {
            category: "Tax Benefits",
            benefits: [
                "0% corporate tax rate",
                "No personal income tax",
                "No capital gains tax",
                "No withholding tax"
            ]
        },
        {
            category: "Privacy & Protection",
            benefits: [
                "Enhanced confidentiality",
                "Asset protection",
                "International business privacy",
                "Secure banking options"
            ]
        },
        {
            category: "Business Structure",
            benefits: [
                "100% foreign ownership",
                "No physical office requirement",
                "Minimal reporting requirements",
                "Simple corporate structure"
            ]
        },
        {
            category: "International Operations",
            benefits: [
                "Access to global markets",
                "International banking facilities",
                "Currency flexibility",
                "Remote business management"
            ]
        }
    ];

    const statistics = [
        {icon: Building, value: "50+", label: "Free Zones", color: "blue"},
        {icon: Briefcase, value: "40k+", label: "Companies Registered", color: "green"},
        {icon: Users, value: "100k+", label: "Entrepreneurs", color: "purple"},
        {icon: Clock, value: "24/7", label: "Support Available", color: "orange"}
    ];

    const services = [
        {
            title: "Business Setup Consulting",
            description: "Expert guidance on choosing the right business structure and location",
            icon: Building,
            features: ["Zone Selection", "License Type Advisory", "Cost Analysis", "Timeline Planning"]
        },
        {
            title: "Legal Documentation",
            description: "Complete assistance with all legal and regulatory requirements",
            icon: ShieldCheck,
            features: ["License Processing", "Visa Services", "Corporate Documents", "Government Approvals"]
        },
        {
            title: "Banking Support",
            description: "Assistance with corporate banking solutions and account setup",
            icon: DollarSign,
            features: ["Account Opening", "Trade Finance", "Credit Facilities", "Payment Solutions"]
        },
        {
            title: "Business Growth Services",
            description: "Ongoing support for business expansion and development",
            icon: LineChart,
            features: ["Market Analysis", "Growth Strategy", "Network Access", "Investment Advisory"]
        }
    ];

    const testimonials = [
        {
            name: "Sarah Johnson",
            company: "Tech Solutions LLC",
            text: "Setting up our tech company in Dubai's Free Zone was seamless thanks to this incredible service. The guidance we received was invaluable.",
            rating: 5,
            image: "/api/placeholder/64/64"
        },
        {
            name: "Mohammed Al-Rashid",
            company: "Global Trading Co.",
            text: "The mainland setup process was extremely professional. Their expertise in local regulations made everything straightforward.",
            rating: 5,
            image: "/api/placeholder/64/64"
        },
        {
            name: "David Chen",
            company: "Innovation Hub FZ-LLC",
            text: "Outstanding service! They helped us navigate through all complexities of business setup in Dubai with great expertise.",
            rating: 5,
            image: "/api/placeholder/64/64"
        }
    ];

    const faqs = [
        {
            question: "What's the difference between Free Zone and Mainland?",
            answer: "Free Zones offer 100% foreign ownership and tax benefits but limit business to within the zone. Mainland companies can trade anywhere in the UAE but may require local partnership."
        },
        {
            question: "How long does it take to set up a company?",
            answer: "Timeline varies by zone and type: Free Zone (1-2 weeks), Mainland (2-3 weeks), Offshore (3-4 weeks). Exact timing depends on documentation and requirements."
        },
        {
            question: "What are the minimum capital requirements?",
            answer: "Capital requirements vary by zone and activity. Some zones have no minimum, while others may require proof of capital from AED 50,000 upwards."
        },
        {
            question: "Can I sponsor employees?",
            answer: "Yes, both Free Zone and Mainland companies can sponsor employees. Visa quota depends on company type, size, and location."
        }
    ];

    const othersBenefits = [
        {
            category: "International Access",
            benefits: [
                "Strategic gateway to Middle East markets",
                "Access to 2+ billion consumers",
                "International trade agreements",
                "Global logistics network"
            ]
        },
        {
            category: "Business Environment",
            benefits: [
                "Political and economic stability",
                "World-class infrastructure",
                "Multi-cultural business community",
                "Smart city initiatives"
            ]
        },
        {
            category: "Support Services",
            benefits: [
                "24/7 government services",
                "Digital transformation support",
                "Business development resources",
                "Professional networking events"
            ]
        },
        {
            category: "Quality of Life",
            benefits: [
                "High standard of living",
                "International education options",
                "Premium healthcare facilities",
                "Safe living environment"
            ]
        }
    ];

    return (
        <div className="min-h-screen bg-gray-50">
            <Helmet>
                <title>Business Zones in Dubai - Dubai Business Solutions</title>
                <meta name="description"
                      content="Dubai Business Solutions offers tailored business zones designed to accelerate your growth. From tech innovation to real estate, Dubai offers unparalleled opportunities for global entrepreneurs."/>
                <link rel="canonical" href="https://www.businesssolutionsdubai.com"/>
                <meta name="robots" content="index, follow"/>
                <meta name="keywords"
                      content="Business Zones, Dubai Business Solutions, Dubai Business, Dubai Startups, Dubai Ventures, Dubai Investment, Dubai Real Estate, Dubai Tech, Dubai Finance, Dubai Retail, Dubai E-commerce, Dubai General Trading, Dubai Banking, Dubai Business Consulting, Business Setup, Business Solutions, Business Services, Business Strategy, Business Planning, Business Development, Business Growth, Business Opportunities, Global Business, Global Entrepreneurship, Global Growth, Global Innovation, Global Investment, Global Real Estate, Global Tech, Global Finance, Global Retail, Global E-commerce, Global Trading, Global Banking, Global Business Consulting, Setup Business, Startup Business, Venture Business, Investment Business, Real Estate Business, Tech Business, Finance Business, Retail Business, E-commerce Business, Trading Business, Banking Business, Consulting Business"/>
                <meta name="lenguage" content="english"/>
                <meta name="publisher" content="Business Solutions Dubai"/>
                <link rel="preload" href="/Assets/Images/image-section-purpose.webp" as="image"/>
            </Helmet>
            <section style={{backgroundImage: `url(${trial2})`}}
                     className="bg-cover bg-center bg-no-repeat h-[800px] md:h-[90vh] pt-[90px] flex justify-left align-center">
                <div
                    className="flex justify-center items-left flex-col relative overflow-hidden w-[98%] md:w-[60%] m-4 md:m-16">

                    <h1 className="text-5xl md:text-7xl font-bold text-gray-800 mb-4 text-left relative z-10 text-transparent bg-clip-text bg-gradient-to-r from-gray-200 to-white">
                        Business Zones in <br/> The United Arab Emirates
                    </h1>
                    <p className="text-xl text-white w-[80%] md:max-w-2xl text-left mb-4 relative z-10">
                        Explore the perfect business environment for your company in Dubai's strategic locations
                    </p>
                    <a
                        href="https://wa.link/txunpz"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="bg-blue-500 text-white px-8 py-3 mx-2 md:mx-8 rounded-full hover:bg-blue-600 transition-all duration-300 transform hover:scale-105 flex items-center gap-2 w-[200px]"
                    >
                        Get Started
                        <ArrowRight className="h-5 w-5"/>
                    </a>
                </div>
            </section>

            <div className="w-full bg-white py-12 shadow-sm">
                <div className="max-w-6xl mx-auto px-6">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                        {statistics.map((stat, index) => (
                            <div
                                key={index}
                                className="flex flex-col items-center p-6 rounded-lg hover:shadow-lg transition-all duration-300"
                            >
                                <stat.icon className={`h-8 w-8 text-${stat.color}-500 mb-4`}/>
                                <h3 className="text-3xl font-bold text-gray-800 mb-2">{stat.value}</h3>
                                <p className="text-gray-600 text-center">{stat.label}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* Enhanced Tabs Section */}
            <div className="w-full max-w-6xl mx-auto p-6">
                <div
                    className="flex justify-start mb-6 border-b overflow-x-auto h-[120px] overflow-y-hidden scroll-smooth scrollbar-hide">
                    <div className="flex space-x-4 pl-4">
                        <button
                            className={`flex items-center md:px-8 px-4 py-4 transition-all duration-300 ${
                                activeTab === 'others'
                                    ? 'border-b-2 border-blue-500 text-blue-500 scale-105'
                                    : 'text-gray-600 hover:text-blue-400'
                            }`}
                            onClick={() => setActiveTab('others')}
                        >
                            <Globe className="mr-2 h-5 w-5"/>
                            Overview
                        </button>
                        <button
                            className={`flex items-center md:px-8 px-4 py-4 transition-all duration-300 ${
                                activeTab === 'free-zones'
                                    ? 'border-b-2 border-blue-500 text-blue-500 scale-105'
                                    : 'text-gray-600 hover:text-blue-400'
                            }`}
                            onClick={() => setActiveTab('free-zones')}
                        >
                            <Building className="mr-2 h-5 w-5"/>
                            Freezones
                        </button>
                        <button
                            className={`flex items-center md:px-8 px-4 py-4 transition-all duration-300 ${
                                activeTab === 'mainland'
                                    ? 'border-b-2 border-blue-500 text-blue-500 scale-105'
                                    : 'text-gray-600 hover:text-blue-400'
                            }`}
                            onClick={() => setActiveTab('mainland')}
                        >
                            <Building2 className="mr-2 h-5 w-5"/>
                            Mainland
                        </button>
                        <button
                            className={`flex items-center md:px-8 px-4 py-4 transition-all duration-300 ${
                                activeTab === 'offshore'
                                    ? 'border-b-2 border-blue-500 text-blue-500 scale-105'
                                    : 'text-gray-600 hover:text-blue-400'
                            }`}
                            onClick={() => setActiveTab('offshore')}
                        >
                            <Ship className="mr-2 h-5 w-5"/>
                            Offshore
                        </button>
                    </div>
                </div>


                {/* New Others Tab Content */}
                <div
                    className={`transition-opacity duration-300 ${activeTab === 'others' ? 'opacity-100' : 'opacity-0 hidden'}`}>
                    <div className="bg-white rounded-lg shadow-lg p-6">
                        <div className="mb-8">
                            <h2 className="text-3xl font-bold mb-3 text-blue-600">
                                Why Choose Dubai?
                            </h2>
                            <p className="text-gray-600 text-lg">
                                Dubai offers a perfect blend of opportunity, infrastructure, and innovation for your
                                business success
                            </p>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {othersBenefits.map((category, index) => (
                                <div
                                    key={category.category}
                                    className="border rounded-lg p-6 hover:shadow-lg transition-all duration-300 hover:scale-[1.02]"
                                    style={{
                                        animation: `fadeIn 0.5s ease-out ${index * 0.1}s`
                                    }}
                                >
                                    <h3 className="text-xl font-semibold mb-4 text-blue-600">
                                        {category.category}
                                    </h3>
                                    <ul className="space-y-3">
                                        {category.benefits.map((benefit, index) => (
                                            <li key={index} className="flex items-center gap-3">
                                                <CheckCircle className="h-4 w-4 text-green-500 flex-shrink-0"/>
                                                <span className="text-gray-600">{benefit}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                <div
                    className={`transition-opacity duration-300 ${activeTab === 'free-zones' ? 'opacity-100' : 'opacity-0 hidden'}`}>
                    <div className="bg-white rounded-lg shadow-lg p-6">
                        <div className="mb-8">
                            <h2 className="text-3xl font-bold mb-3 text-blue-600">
                                Free Zones in Dubai
                            </h2>
                            <p className="text-gray-600 text-lg">
                                Discover Dubai's specialized business districts offering 100% foreign ownership and tax
                                benefits
                            </p>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {freeZones.map((zone, index) => (
                                <div
                                    key={zone.name}
                                    className="border rounded-lg p-6 hover:shadow-lg transition-all duration-300 hover:scale-[1.02]"
                                    style={{
                                        animation: `fadeIn 0.5s ease-out ${index * 0.1}s`
                                    }}
                                >
                                    <h3 className="text-xl font-semibold mb-4 text-blue-600">
                                        {zone.name}
                                    </h3>

                                    <div className="mb-4">
                                        <h4 className="font-semibold mb-2 text-gray-700">Industries</h4>
                                        <div className="flex flex-wrap gap-2">
                                            {zone.industries.map((industry, index) => (
                                                <span key={index}
                                                      className="bg-blue-50 text-blue-600 px-3 py-1 rounded-full text-sm">
                                                    {industry}
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                    <div className="mb-4">
                                        <h4 className="font-semibold mb-2 text-gray-700">Key Benefits</h4>
                                        <ul className="space-y-2">
                                            {zone.benefits.map((benefit, index) => (
                                                <li key={index} className="flex items-center gap-2">
                                                    <CheckCircle className="h-4 w-4 text-green-500 flex-shrink-0"/>
                                                    <span className="text-gray-600 text-sm">{benefit}</span>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                    {/* <p className="text-gray-500 mb-4">📍 {zone.location}</p> */}
                                    <p className="text-sm text-gray-500">
                                        ⏱️ Average setup time: {zone.setupTime}
                                    </p>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                <div
                    className={`transition-opacity duration-300 ${activeTab === 'mainland' ? 'opacity-100' : 'opacity-0 hidden'}`}>
                    <div className="bg-white rounded-lg shadow-lg p-6">
                        <div className="mb-8">
                            <h2 className="text-3xl font-bold mb-3 text-blue-600">
                                Mainland Dubai
                            </h2>
                            <p className="text-gray-600 text-lg">
                                Establish your business in Dubai's mainland territory with full market access
                            </p>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {mainlandBenefits.map((category, index) => (
                                <div
                                    key={category.category}
                                    className="border rounded-lg p-6 hover:shadow-lg transition-all duration-300 hover:scale-[1.02]"
                                    style={{
                                        animation: `fadeIn 0.5s ease-out ${index * 0.1}s`
                                    }}
                                >
                                    <h3 className="text-xl font-semibold mb-4 text-blue-600">
                                        {category.category}
                                    </h3>
                                    <ul className="space-y-3">
                                        {category.benefits.map((benefit, index) => (
                                            <li key={index} className="flex items-center gap-3">
                                                <CheckCircle className="h-4 w-4 text-green-500 flex-shrink-0"/>
                                                <span className="text-gray-600">{benefit}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                <div
                    className={`transition-opacity duration-300 ${activeTab === 'offshore' ? 'opacity-100' : 'opacity-0 hidden'}`}>
                    <div className="bg-white rounded-lg shadow-lg p-6">
                        <div className="mb-8">
                            <h2 className="text-3xl font-bold mb-3 text-blue-600">
                                Offshore Company
                            </h2>
                            <p className="text-gray-600 text-lg">
                                Set up an international business structure with enhanced privacy and tax benefits
                            </p>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {offshoreBenefits.map((category, index) => (
                                <div
                                    key={category.category}
                                    className="border rounded-lg p-6 hover:shadow-lg transition-all duration-300 hover:scale-[1.02]"
                                    style={{
                                        animation: `fadeIn 0.5s ease-out ${index * 0.1}s`
                                    }}
                                >
                                    <h3 className="text-xl font-semibold mb-4 text-blue-600">
                                        {category.category}
                                    </h3>
                                    <ul className="space-y-3">
                                        {category.benefits.map((benefit, index) => (
                                            <li key={index} className="flex items-center gap-3">
                                                <CheckCircle className="h-4 w-4 text-green-500 flex-shrink-0"/>
                                                <span className="text-gray-600">{benefit}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
            <div
                className="w-full bg-gray-50 py-16 flex justify-center align-center flex-col md:flex-row gap-16 md:gap-0">
                <div className="max-w-4xl mx-auto px-6 w-full md:w-[70vw]">
                    <h2 className="text-3xl font-bold text-center mb-12 text-gray-800">Frequently Asked Questions</h2>
                    <div className="space-y-6">
                        {faqs.map((faq, index) => (
                            <div
                                key={index}
                                className="bg-white p-6 rounded-lg shadow-sm hover:shadow-md transition-all duration-300"
                            >
                                <h3 className="text-lg font-semibold mb-3 text-gray-800">{faq.question}</h3>
                                <p className="text-gray-600">{faq.answer}</p>
                            </div>
                        ))}
                    </div>
                </div>
                <div className="w-[90%] md:w-[30vw] m-0 m-auto">
                    <img src={trial} alt="dubai-landscape" className="w-full md:w-[80%] rounded-lg" loading="lazy"/>
                </div>
            </div>
            <div className="w-full bg-blue-600 py-16">
                <div className="max-w-4xl mx-auto px-6 text-center text-white">
                    <h2 className="text-3xl font-bold mb-6">Ready to Start Your Business in Dubai?</h2>
                    <p className="text-xl mb-8">Let our experts guide you through every step of the process</p>
                    <a
                        href="https://wa.link/txunpz"
                        className="w-[220px] bg-white text-blue-600 px-8 py-3 rounded-full hover:bg-gray-100 transition-all duration-300 transform hover:scale-105 flex items-center gap-2 mx-auto"
                    >
                        Contact Us Now
                        <ArrowRight className="h-5 w-5"/>
                    </a>
                </div>
            </div>
            <div className="w-full bg-white py-16">
                <div className="max-w-6xl mx-auto px-6">
                    <h2 className="text-3xl font-bold text-center mb-12 text-gray-800">What Our Clients Say</h2>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                        {testimonials.map((testimonial, index) => (
                            <div
                                key={index}
                                className="bg-gray-50 p-6 rounded-lg hover:shadow-lg transition-all duration-300"
                            >
                                <div className="flex items-center mb-4">

                                    <div>
                                        <h4 className="font-semibold">{testimonial.name}</h4>
                                        <p className="text-sm text-gray-600">{testimonial.company}</p>
                                    </div>
                                </div>
                                <p className="text-gray-600 mb-4">{testimonial.text}</p>
                                <div className="flex text-yellow-400">
                                    {[...Array(testimonial.rating)].map((_, i) => (
                                        <Award key={i} className="h-5 w-5"/>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );

};

export default DubaiZonesInfo;